import { DataProcessor, percent } from '@amcharts/amcharts5';
import {
  BackgroundGradientType,
  Constant,
  SeriesType,
} from '@/modules/core/legacy/charts/am5/charts.constants';
import { ColumnSeries } from '@amcharts/amcharts5/xy';
import { RadarColumnSeries } from '@amcharts/amcharts5/radar';
import { useXYAxes } from '@/modules/core/legacy/charts/am5/base/composables/axis/useXYAxes';
import { color } from '@/modules/core/legacy/charts/am5/charts.helper';
import { omitNils } from '@/modules/core/app/utils/ObjectUtil';
import { getChartConfig } from '@/modules/core/legacy/charts/am5/bar/config/chartconfig';
import { useGradient } from '@/modules/core/legacy/charts/am5/base/composables/fills/useGradient';
import { ColumnFormat, ComparisonTypes } from '@/modules/core/app/constants/data.constants';
import { useXYTooltip } from '@/modules/core/legacy/charts/am5/base/composables/useXYTooltip';
import { useAxes } from '@/modules/core/legacy/charts/am5/base/composables/axis/useAxes';
import { useNumberFormatter } from '@/modules/core/legacy/charts/am5/gauge/composables/useNumberFormatter';
import { isNil, isEmpty } from 'lodash';
import { useComparison } from '@/modules/core/legacy/charts/am5/line/composables/useComparison';
import { PlotType } from '@/modules/ta/widget/widget.constants';

export function useXYSeries(context) {
  const { root, chart, config, isDarkTheme } = context();
  const { getXAxis, getYAxis, getSeriesAxesProps } = useXYAxes(context);
  const { getCategoryAxis } = useAxes(context);
  const { createLinearGradient } = useGradient(context);
  const { getNewDefaultTooltip } = useXYTooltip(context);
  const { getLegendPlaceholderString } = useNumberFormatter(context);
  const { calculateDeltaValue, createBulletsForComparison } = useComparison(context);

  function createAllSeries() {
    const { isStacked, isFullStacked, series } = config.value;
    const seriesIterator = isStacked || isFullStacked ? series.slice().reverse() : series;
    seriesIterator.forEach((props) => {
      createSeries(props);
    });
  }

  function createColumnSeries() {
    const { isStacked, isFullStacked, series } = config.value;
    const seriesIterator = isStacked || isFullStacked ? series.slice().reverse() : series;
    seriesIterator.forEach((props) => {
      if (props.seriesType === SeriesType.COLUMN) createSeries(props);
    });
  }

  function applyDataToAllSeries() {
    if (!isNil(config.value.series)) {
      checkNullForMetrics();
    }

    chart.value.series.values.forEach((series) => series.data.setAll(config.value.data));
  }

  function isRotatedGraph() {
    return config.value.isRadialBar || config.value.isRotated;
  }

  function createSeries(props) {
    const SeriesClass = getSeriesClass(props.seriesType);
    const xAxis = getXAxis(isRotatedGraph() ? props.axisIndex : props.categoryAxisIndex);
    const yAxis = getYAxis(isRotatedGraph() ? props.categoryAxisIndex : props.axisIndex);
    const series = chart.value.series.push(
      SeriesClass.new(root.value, {
        xAxis,
        yAxis,
        ...getGenericSeriesProps(props),
        ...getTypeSpecificSeriesProps(props),
        adjustBulletPosition: false,
      })
    );

    series.set(Constant.USER_DATA, props);

    if (props.seriesType === SeriesType.COLUMN) {
      const { isRotated, fillType, isLayered, lollipopChart, isCurvedColumns, isClustered } =
        config.value;

      const dimensionProp = isRotated ? Constant.HEIGHT : Constant.WIDTH;
      const dimensionValue = lollipopChart ? 1 : null;
      series.columns.template.setAll(
        omitNils({
          [dimensionProp]: isLayered ? percent(props.seriesWidth) : dimensionValue,
        })
      );

      const { shadowProps, cornerProps } = getChartConfig(config.value);
      if (fillType !== BackgroundGradientType.SOLID && !lollipopChart) {
        const angle = fillType === BackgroundGradientType.LINEAR_Y_GRADIENT ? 0 : 90;
        const fillGradient = createLinearGradient(['', config.value.gradientColor], angle);
        series.columns.template.setAll({ fillGradient });
      }
      series.columns.template.setAll({
        ...shadowProps,
        ...cornerProps,
        opacity: props.opacity,
        fillOpacity: config.value.isSparkLine ? props.fillOpacity : props.opacity,
      });

      if (isCurvedColumns && (isLayered || isClustered)) {
        const axis = getCategoryAxis();
        axis.get(Constant.RENDERER).set(Constant.CELL_START_LOCATION, 0);
        axis.get(Constant.RENDERER).set(Constant.CELL_END_LOCATION, 1);
        series.columns.template.set(Constant.DRAW, (display, target) => {
          const w = target.getPrivate(Constant.WIDTH, 0);
          const h = target.getPrivate(Constant.HEIGHT, 0);
          if (isRotated) {
            display.moveTo(0, 0);
            display.bezierCurveTo(w / 4, h / 4, w, h / 4, w, h / 2);
            display.bezierCurveTo(w, h - h / 4, w - w / 4, h - h / 4, 0, h);
          } else {
            display.moveTo(0, h);
            display.bezierCurveTo(w / 4, h, w / 4, 0, w / 2, 0);
            display.bezierCurveTo(w - w / 4, 0, w - w / 4, h, w, h);
          }
        });
        const propName = isRotated ? Constant.HEIGHT : Constant.WIDTH;
        series.columns.template.setAll({
          [propName]: percent(120),
        });
      }
    }

    if (
      config.value.hasRankingMetrics &&
      props.seriesType === SeriesType.COLUMN &&
      props.isRankingMetric
    ) {
      invertRankingColumns(series);
    }

    if (props.fill && !config.value.isSparkLine) {
      series.fills.template.setAll({
        visible: true,
        fillOpacity: props.fillOpacity,
      });
    }

    series.data.processor = DataProcessor.new(root.value, {
      numericFields: [props.value],
    });
    return series;
  }

  /**
   * By default, "inverse" columns come down from the top
   * We want them to rise up from the bottom so take the height (or width if rotated)
   * and minus that from the bounds of the parent's parent (the container)
   * That leaves us with a column of the correct size
   * @param series
   */
  function invertRankingColumns(series) {
    series.columns.template.set(Constant.DRAW, (display, target) => {
      const h = target.getPrivate(Constant.HEIGHT, 0);
      const w = target.getPrivate(Constant.WIDTH, 0);
      const absH = Math.abs(h);

      let originX = 0;
      let originY = target.getPrivate(Constant.Y, 0);
      let targetWidth = target.getPrivate(Constant.WIDTH, 0);
      let targetHeight = target.parent.parent._localBounds.bottom - absH;

      if (config.value.isRotated) {
        originX = -Math.abs(target.parent.parent._localBounds.right);
        originY = target.getPrivate(Constant.Y, 0);
        targetWidth = target.parent.parent._localBounds.right + w;
        targetHeight = target.getPrivate(Constant.HEIGHT, 0);
      }

      display.drawRect(originX, originY, targetWidth, targetHeight);
    });
  }

  function getGenericSeriesProps(props) {
    const { hasTooltip } = config.value;
    const tooltip = hasTooltip ? getNewDefaultTooltip() : null;

    return omitNils({
      name: props.name,
      fill: color(props.color),
      legendLabelText: props.name,
      legendValueText: getLegendPlaceholderString(props.value, props.dataItemFormat),
      // empty value so it doesn't display a currency symbol after mouseout event
      legendRangeValueText: '',
      maskBullets: true,
      tooltip,
    });
  }

  function getTypeSpecificSeriesProps(props) {
    const seriesAxesProps = getSeriesAxesProps(props);
    switch (props.seriesType) {
      case SeriesType.COLUMN: {
        const { seriesOptions, layeredProps } = getChartConfig(config.value);
        if (props.dataItemFormat === ColumnFormat.FORMAT_TIME) {
          delete seriesOptions.stacked;
        }
        return omitNils({
          ...seriesAxesProps,
          ...seriesOptions,
          ...layeredProps,
          stroke: isDarkTheme.value ? props.strokeColor : color(props.color),
          locationX: props.locationX,
        });
      }
      default:
        return {};
    }
  }

  function getSeriesClass(type) {
    const isRadarCharts = config.value.isRadialHistogram || config.value.isRadialBar;
    switch (type) {
      case SeriesType.COLUMN:
        return isRadarCharts ? RadarColumnSeries : ColumnSeries;
      default:
        // eslint-disable-next-line tap/no-raw-text-js
        Logger.log(`SeriesType ${type} is not supported`, Logger.LEVEL_ERROR);
    }
  }

  /**
   * In Certain cases null values of the metrics not showing in the chart, so assigning null values to 0 only for metrics
   */
  function checkNullForMetrics() {
    const seriesKeys = config.value.series.map((series) => series.value);
    config.value.data.forEach((data) => {
      seriesKeys.forEach((key) => {
        data[key] = isNil(data[key]) || Number.isNaN(data[key]) ? undefined : data[key]; // Using nullish coalescing operator
      });
    });
  }

  function isDateInCurrentData() {
    const { currentDataFormat } = config.value;
    return (
      currentDataFormat === ColumnFormat.FORMAT_DATE ||
      currentDataFormat === ColumnFormat.FORMAT_DATETIME
    );
  }

  function isComparisonWithData() {
    const { hasComparison, data, dataSelected, comparisonType } = config.value;
    return (
      hasComparison &&
      comparisonType === ComparisonTypes.FINAL_INITIAL_VALUE &&
      data.length &&
      dataSelected
    );
  }

  function isDataWithDate() {
    return isComparisonWithData() && isDateInCurrentData();
  }

  function applyComparisonDifference() {
    const { data, dataSelected, comparisonOverWrite, plotType } = config.value;

    if (isDataWithDate()) {
      const dataLength = data.length;
      const firstValue = data[0];
      const lastValue = data[dataLength - 1];
      let percentArray = [];
      const duplicateCheck = [];
      const isNotDateOrStringFormat = (format) =>
        ![ColumnFormat.FORMAT_DATETIME, ColumnFormat.FORMAT_STRING].includes(format);
      const isStackedPlotType = (plot) =>
        [PlotType.FULL_STACKED_V2, PlotType.STACKED_V2].includes(plot);

      dataSelected?.forEach((metric) => {
        const { field, format, label } = metric;
        let statusValueLocation = false;
        let toolTipText = '';
        let calculatedInitialPercentValue = 0;
        let didCurrentValueChange = true;
        let arrowDirection = true;
        // determine whether red or green color
        const determineValueChange = (initialValue, finalValue, includesField) => {
          if (initialValue > finalValue) {
            return !comparisonOverWrite.includes(includesField);
          }
          return comparisonOverWrite.includes(includesField);
        };

        if (isNotDateOrStringFormat(format)) {
          if (plotType === PlotType.FULL_STACKED_V2) {
            const { initialTotal, finalTotal } = dataSelected.reduce(
              (totals, { field: loopField, format: loopFormat }) => {
                if (isNotDateOrStringFormat(loopFormat)) {
                  totals.initialTotal += firstValue[loopField];
                  totals.finalTotal += lastValue[loopField];
                }
                return totals;
              },
              { initialTotal: 0, finalTotal: 0 }
            );
            const initialPercentTotal = (firstValue[field] / initialTotal) * 100;
            const finalPercentTotal = (lastValue[field] / finalTotal) * 100;
            calculatedInitialPercentValue = Math.abs(
              Number(finalPercentTotal - initialPercentTotal).toFixed(2)
            );
            didCurrentValueChange = determineValueChange(
              initialPercentTotal,
              finalPercentTotal,
              field
            );
            arrowDirection = finalPercentTotal > initialPercentTotal;
          } else {
            // calculates percentage value except 100% stacked
            calculatedInitialPercentValue = calculateDeltaValue(
              lastValue[field],
              firstValue[field],
              { field, format }
            );

            // check for N/A only for initial value
            if (firstValue[field] === 0) {
              calculatedInitialPercentValue = Constant.NOT_APPLICABLE;
              const text = firstValue[field] === 0 ? Constant.INITIAL : Constant.FINAL;
              const mathOperator = firstValue[field] > lastValue[field] ? '-' : '+';
              // eslint-disable-next-line tap/no-raw-text-js
              toolTipText = `As ${text} value=0, % change is N/A. Change is ${mathOperator}${Math.max(
                firstValue[field],
                lastValue[field]
              )}`;
            }
            // if final value is 0 we need to show 100% decrease
            if (lastValue[field] === 0 && firstValue[field] !== 0) {
              calculatedInitialPercentValue = 100;
            }
            didCurrentValueChange = determineValueChange(
              firstValue[field],
              lastValue[field],
              field
            );
            arrowDirection = firstValue[field] <= lastValue[field];
          }

          statusValueLocation = duplicateCheck.includes(lastValue[field]);
          if (isEmpty(duplicateCheck) || !duplicateCheck.includes(lastValue[field])) {
            duplicateCheck.push(lastValue[field]);
          }

          const createCalcObject = (
            percentValue,
            status,
            labels,
            statusLocation,
            tooltip,
            toggleArrowDirection
          ) => ({
            percentValue,
            status,
            currentLastValue: lastValue[field],
            toggleArrowDirection,
            format,
            field,
            labels,
            statusLocation,
            tooltip,
          });

          percentArray.push(
            createCalcObject(
              calculatedInitialPercentValue,
              didCurrentValueChange,
              label,
              statusValueLocation,
              toolTipText,
              arrowDirection
            )
          );
        }
      });
      // reversing array due to stacked series is in reverse
      percentArray = isStackedPlotType(plotType) ? percentArray.reverse() : percentArray;
      createBulletsForComparison(percentArray);
    }
  }

  return {
    createAllSeries,
    applyDataToAllSeries,
    createSeries,
    createColumnSeries,
    applyComparisonDifference,
    isDataWithDate,
  };
}
